import {
  deleteBlockWithTrashcan,
  deleteElementWithTrashcan,
} from '@blocksuite/affine-block-streamlain';
import { toast } from '@blocksuite/affine-components/toast';
import type { SurfaceElementModelMap } from '@blocksuite/affine-model';
import type {
  BlockDeletionOptions,
  BlockDeletionOutcome,
  BlockDeletionResult,
} from '@blocksuite/affine-shared/commands';
import {
  EditPropsStore,
  UserProvider,
} from '@blocksuite/affine-shared/services';
import { type Container } from '@blocksuite/global/di';
import { type BlockStdScope, StdIdentifier } from '@blocksuite/std';
import {
  GfxBlockElementModel,
  GfxControllerIdentifier,
  type GfxModel,
  GfxPrimitiveElementModel,
  isGfxGroupCompatibleModel,
  loadGroupMemberParts,
} from '@blocksuite/std/gfx';
import {
  type BlockModel,
  Extension,
  placeCanvasWorldXYWH,
} from '@blocksuite/store';
import type { PageElementUnit } from '@streamlain/database-identity';

import type { SurfaceBlockModel } from '../surface-model';
import { getLastPropsKey } from '../utils/get-last-props-key';
import { EdgelessCRUDIdentifier } from './crud-identifier';
import { isConnectable, isNoteBlock } from './query';

/** The ids whose connectors leave with an element: a mind map's nodes too. */
function connectorEnds(element: GfxModel): string[] {
  if (!isConnectable(element)) return [];
  return element instanceof GfxPrimitiveElementModel &&
    element.type === 'mindmap' &&
    isGfxGroupCompatibleModel(element)
    ? [element.id, ...element.childIds]
    : [element.id];
}

/** connectorEnds of an element and of all members of its groups and frames. */
function treeConnectorEnds(element: GfxModel): string[] {
  const own = connectorEnds(element);
  return isGfxGroupCompatibleModel(element) &&
    !(element instanceof GfxPrimitiveElementModel && element.type === 'mindmap')
    ? [...own, ...element.childElements.flatMap(treeConnectorEnds)]
    : own;
}

export { EdgelessCRUDIdentifier } from './crud-identifier';

export class EdgelessCRUDExtension extends Extension {
  constructor(readonly std: BlockStdScope) {
    super();
  }

  static override setup(di: Container) {
    di.add(this, [StdIdentifier]);
    di.addImpl(EdgelessCRUDIdentifier, provider => provider.get(this));
  }

  private get _gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  private get _surface() {
    return this._gfx.surface as SurfaceBlockModel | null;
  }

  deleteElements = (
    elements: GfxModel[],
    options: BlockDeletionOptions = {}
  ): Promise<BlockDeletionOutcome[]> => {
    const surface = this._surface;
    if (!surface) {
      console.error('surface is not initialized');
      return Promise.resolve(elements.map(() => 'rejected'));
    }
    const deletedBy =
      options.deletedBy ??
      this.std.getOptional(UserProvider)?.currentUserInfo$.peek()?.id;
    // A group or frame leaves with all its members, an element with all its
    // connectors: also those still loading.
    const remove = () =>
      Promise.all(
        this._deleteLoadedElements(surface, elements, options, deletedBy)
      );
    const loadConnectors = () => {
      const loading = this._loadConnectorParts(elements);
      return loading ? loading.then(remove) : remove();
    };
    const loading = loadGroupMemberParts(this.std.store, elements);
    return loading ? loading.then(loadConnectors) : loadConnectors();
  };

  private _loadConnectorParts(elements: GfxModel[]): Promise<void> | null {
    const structure = this.std.store.pageStructure;
    const ends = elements.flatMap(element => treeConnectorEnds(element));
    const loadFields = () => {
      const ids = ends.flatMap(id => [...structure.connectorsOf(id)]);
      return structure.fieldsLoaded(ids) ? null : structure.loadPartsOf(ids);
    };
    const loading = structure.loadConnectorParts(ends);
    return loading
      ? loading.then(async () => {
          await loadFields();
        })
      : loadFields();
  }

  private _deleteLoadedElements(
    surface: SurfaceBlockModel,
    elements: GfxModel[],
    options: BlockDeletionOptions,
    deletedBy: string | undefined
  ): BlockDeletionResult[] {
    return elements.map(element => {
      const connectors = [
        ...new Map(
          connectorEnds(element)
            .flatMap(id => [...surface.getConnectors(id)])
            .map(connector => [connector.id, connector])
        ).values(),
      ];
      // The groups they leave; the trash entry brings them back there.
      const groups = Object.fromEntries(
        [element.id, ...connectors.map(connector => connector.id)].flatMap(
          id => {
            const group = surface.getGroup(id);
            // Only real groups; frames and mind maps keep their own membership.
            return group && 'type' in group && group.type === 'group'
              ? [[id, group.id]]
              : [];
          }
        )
      );
      const finish = (outcome: BlockDeletionOutcome): BlockDeletionResult => {
        // Keep connectors while confirmation is open, after cancellation,
        // and while a queued server trash keeps the element until its
        // receipt: the server's trash takes them into the table's entry.
        if (outcome !== 'deleted' || this.getElementById(element.id))
          return outcome;
        // They are in the trash entry of the element already.
        const removed = connectors.map(connector =>
          this.removeElement(connector.id, { moving: true })
        );
        return removed.some(result => typeof result !== 'string')
          ? Promise.all(removed).then(() => outcome)
          : outcome;
      };
      const result = this.removeElement(element, {
        ...options,
        deletedBy,
        connectors: options.moving
          ? undefined
          : connectors.map(connector => connector.serialize()),
        groups:
          options.moving || Object.keys(groups).length === 0
            ? undefined
            : groups,
      });
      return typeof result === 'string' ? finish(result) : result.then(finish);
    });
  }

  addBlock = (
    flavour: string,
    props: Record<string, unknown>,
    parentId?: string | BlockModel,
    parentIndex?: number
  ) => {
    const gfx = this.std.get(GfxControllerIdentifier);
    const key = getLastPropsKey(flavour, props);
    if (key) {
      props = this.std.get(EditPropsStore).applyLastProps(key, props);
    }

    const nProps = {
      ...props,
      index: gfx.layer.generateIndex(),
      ...(props.xywh === undefined
        ? {}
        : { xywh: placeCanvasWorldXYWH(props.xywh) }),
    };

    return this.std.store.addBlock(
      flavour as never,
      nProps,
      parentId,
      parentIndex
    );
  };

  addElement = <T extends Record<string, unknown>>(
    type: string,
    props: T,
    unit?: PageElementUnit
  ) => {
    const surface = this._surface;
    if (!surface) {
      console.error('surface is not initialized');
      return;
    }

    const gfx = this.std.get(GfxControllerIdentifier);
    const key = getLastPropsKey(type, props);
    if (key) {
      props = this.std.get(EditPropsStore).applyLastProps(key, props) as T;
    }

    const nProps = {
      ...props,
      type,
      index: props.index ?? gfx.layer.generateIndex(),
      ...(props.xywh === undefined
        ? {}
        : { xywh: placeCanvasWorldXYWH(props.xywh) }),
    };

    return surface.addElement(nProps, unit);
  };

  updateElement = (id: string, props: Record<string, unknown>) => {
    const surface = this._surface;
    if (!surface) {
      console.error('surface is not initialized');
      return;
    }

    const element = this._surface.getElementById(id);
    if (element) {
      const key = getLastPropsKey(element.type, {
        ...element.yMap.toJSON(),
        ...props,
      });
      key && this.std.get(EditPropsStore).recordLastProps(key, props);
      this._surface.updateElement(id, props);
      return;
    }

    const block = this.std.store.getModelById(id);
    if (block) {
      const key = getLastPropsKey(block.flavour, {
        ...block.yBlock.toJSON(),
        ...props,
      });
      key && this.std.get(EditPropsStore).recordLastProps(key, props);
      this.std.store.updateBlock(block, props);
    }
  };

  getElementById(id: string): GfxModel | null {
    const surface = this._surface;
    if (!surface) {
      return null;
    }
    const el = surface.getElementById(id) ?? this.std.store.getModelById(id);
    return el as GfxModel | null;
  }

  getElementsByType<K extends keyof SurfaceElementModelMap>(
    type: K
  ): SurfaceElementModelMap[K][] {
    if (!this._surface) {
      return [];
    }
    return this._surface.getElementsByType(type);
  }

  removeElement(
    id: string | GfxModel,
    options: BlockDeletionOptions = {}
  ): BlockDeletionResult {
    id = typeof id === 'string' ? id : id.id;
    if (this.std.store.readonly) return 'cancelled';
    const el = this.getElementById(id);
    if (!el) return 'cancelled';
    if ('flavour' in el && el.flavour === 'affine:ghost') {
      toast(
        this.std.host,
        'Ghost nodes cannot be deleted directly. Restore or permanently delete the original item from the trash.'
      );
      return 'cancelled';
    }
    if (
      isNoteBlock(el) &&
      (this.std.store.root?.children.filter(isNoteBlock).length ?? 0) <= 1
    ) {
      return 'cancelled';
    }
    const remove = (): BlockDeletionResult => {
      if (el instanceof GfxBlockElementModel) {
        const result = deleteBlockWithTrashcan(this.std.store, el, options);
        if (typeof result === 'string') return result;
        return result.then(outcome => {
          if (outcome === 'rejected') {
            toast(this.std.host, 'Failed to move block to trash');
          }
          return outcome;
        });
      }
      if (this._surface?.hasElementById(id)) {
        if (el instanceof GfxPrimitiveElementModel) {
          return deleteElementWithTrashcan(
            this.std.store,
            this._surface,
            el,
            options
          );
        }
        this._surface.deleteElement(id);
        return 'deleted';
      }
      return 'cancelled';
    };
    // A mind map leaves as one trash entry that carries its nodes.
    if (
      isGfxGroupCompatibleModel(el) &&
      !(el instanceof GfxPrimitiveElementModel && el.type === 'mindmap')
    ) {
      // Each member leaves with its own connectors, as a single element does.
      const surface = this._surface;
      if (!surface) return 'rejected';
      const results = this._deleteLoadedElements(
        surface,
        el.childElements,
        { ...options, connectors: undefined },
        options.deletedBy
      );
      const finish = (
        outcomes: BlockDeletionOutcome[]
      ): BlockDeletionResult => {
        // Removing a container must not hard-delete a child that was kept.
        if (outcomes.some(outcome => outcome !== 'deleted')) return 'cancelled';
        // An empty group removes itself once its last child leaves.
        if (!this.getElementById(id)) return 'deleted';
        return remove();
      };
      return results.some(result => typeof result !== 'string')
        ? Promise.all(results).then(finish)
        : finish(results as BlockDeletionOutcome[]);
    }
    return remove();
  }
}
